import { useEffect, useRef } from 'react';
import * as Y from 'yjs';
import { useCanvasReadOnly } from '@/features/canvas/engine/wiring/contexts/canvas-read-only-context';

interface NodeTextEditorProps {
  ytext: Y.Text;
  editing: boolean;
  ariaLabel: string;
  className?: string;
  onBlur?: () => void;
}

/**
 * Write text into the editable. Empty text renders a lone <br> so the editor
 * has a real line box: without it, an empty contenteditable in the flex-column
 * editor has no line for justify-content to center, and the caret falls to the
 * top instead of the vertical-align target (PRD-19). A <br> is measurement-safe
 * — unlike a sized child, it adds no box height to the absolutely-positioned
 * overlay, so it doesn't perturb ReactFlow's node measurement.
 */
function setEditorText(el: HTMLElement, text: string): void {
  if (text === '') {
    el.innerHTML = '<br>';
  } else {
    el.textContent = text;
  }
}

/**
 * Shared contentEditable editor for canvas text nodes (Text, Sticky, Shape).
 * Syncs with a Y.Text, stops keyboard propagation so ReactFlow doesn't
 * intercept Enter/Delete, and reads innerText on input to preserve newlines.
 *
 * Read-only enforcement is centralized HERE (not at every caller): a read-only
 * canvas is never contentEditable and input is ignored, so a node component
 * can't make text editable by forgetting to gate its own edit-entry.
 */
export function NodeTextEditor({ ytext, editing, ariaLabel, className, onBlur }: NodeTextEditorProps) {
  const editorRef = useRef<HTMLDivElement>(null);
  const readOnly = useCanvasReadOnly();
  // A read-only canvas is never in edit mode regardless of what a caller passes.
  const isEditing = editing && !readOnly;

  // Sync Y.Text changes to DOM. Skip when focused to avoid cursor jumps.
  useEffect(() => {
    const refresh = () => {
      const next = ytext.toString();
      const el = editorRef.current;
      if (el && document.activeElement !== el) {
        setEditorText(el, next);
      }
    };
    ytext.observe(refresh);
    refresh();
    return () => ytext.unobserve(refresh);
  }, [ytext]);

  // Focus and place cursor at end when entering edit mode.
  useEffect(() => {
    if (!isEditing) return;
    const el = editorRef.current;
    if (!el) return;
    setEditorText(el, ytext.toString());
    el.focus();
    const range = document.createRange();
    range.selectNodeContents(el);
    range.collapse(false);
    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(range);
  }, [isEditing, ytext]);

  function handleInput(ev: React.FormEvent<HTMLDivElement>) {
    if (readOnly) return;
    const doc = ytext.doc;
    if (!doc) return;
    const raw = ev.currentTarget.innerText ?? '';
    // Chrome appends a trailing \n to innerText for contenteditable; strip it.
    const next = raw.endsWith('\n') ? raw.slice(0, -1) : raw;
    doc.transact(() => {
      ytext.delete(0, ytext.length);
      ytext.insert(0, next);
    }, doc.clientID);
  }

  return (
    <div
      ref={editorRef}
      className={className}
      contentEditable={isEditing ? 'plaintext-only' : 'false'}
      suppressContentEditableWarning
      onInput={handleInput}
      onKeyDown={(e) => e.stopPropagation()}
      onBlur={onBlur}
      aria-label={ariaLabel}
      role="textbox"
      aria-readonly={!isEditing}
    />
  );
}
